import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, ShoppingBag, ClipboardList, Users,
  BarChart3, Settings, LogOut, Menu, ChefHat, Truck,
  DollarSign, Package, Monitor, Gift, Megaphone,
  UserCog, MessageCircle, Instagram, TrendingUp as TrendingUpIcon, UtensilsCrossed
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import IFoodIcon from "@/components/icons/IFoodIcon";
import ThemeToggle from "@/components/ThemeToggle";
import ClearCacheButton from "@/components/ClearCacheButton";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";

interface SidebarItem {
  icon: any;
  label: string;
  path: string;
  featureKey: string | null;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

const sidebarSections: SidebarSection[] = [
  {
    title: "Principal",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", featureKey: null },
      { icon: ClipboardList, label: "Pedidos", path: "/pedidos", featureKey: null },
      { icon: ShoppingBag, label: "Cardápio", path: "/gerenciar-cardapio", featureKey: "menu_digital" },
    ],
  },
  {
    title: "Operações",
    items: [
      { icon: Monitor, label: "PDV", path: "/pdv", featureKey: "pdv" },
      { icon: ChefHat, label: "Cozinha", path: "/cozinha", featureKey: "kitchen_panel" },
      { icon: UtensilsCrossed, label: "Mesas", path: "/mesas", featureKey: "table_management" },
      { icon: Truck, label: "Delivery", path: "/delivery", featureKey: "delivery_management" },
      { icon: Package, label: "Estoque", path: "/estoque", featureKey: "stock_management" },
    ],
  },
  {
    title: "Clientes & Vendas",
    items: [
      { icon: Users, label: "Clientes", path: "/clientes", featureKey: "customer_management" },
      { icon: MessageCircle, label: "Chat Clientes", path: "/chat-clientes", featureKey: null },
      { icon: Gift, label: "Fidelidade", path: "/fidelidade", featureKey: "loyalty_program" },
      { icon: Megaphone, label: "Marketing", path: "/marketing", featureKey: "marketing_ai" },
      { icon: TrendingUpIcon, label: "Crescimento", path: "/crescimento", featureKey: "growth_mode" },
    ],
  },
  {
    title: "Financeiro & Relatórios",
    items: [
      { icon: DollarSign, label: "Financeiro", path: "/financeiro", featureKey: "financial_management" },
      { icon: BarChart3, label: "Relatórios", path: "/relatorios", featureKey: "advanced_reports" },
    ],
  },
  {
    title: "Integrações",
    items: [
      { icon: IFoodIcon, label: "iFood", path: "/ifood", featureKey: "ifood_integration" },
      { icon: Instagram, label: "Instagram Shop", path: "/instagram-shop", featureKey: null },
      { icon: MessageCircle, label: "WhatsApp Catálogo", path: "/whatsapp-catalogo", featureKey: null },
      { icon: UserCog, label: "Funcionários", path: "/funcionarios", featureKey: "staff_management" },
    ],
  },
  {
    title: "Sistema",
    items: [
      { icon: Settings, label: "Configurações", path: "/configuracoes", featureKey: null },
    ],
  },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
  restaurantId?: string | null;
}

const DashboardLayout = ({ children, restaurantId }: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [restaurantName, setRestaurantName] = useState("");
  const planLimits = usePlanLimits(restaurantId || null);
  const { config: platformConfig } = usePlatformConfig();

  useEffect(() => {
    const loadRestaurant = async () => {
      if (!restaurantId) return;
      const { data } = await supabase
        .from("restaurants")
        .select("name")
        .eq("id", restaurantId)
        .maybeSingle();
      if (data) setRestaurantName(data.name);
    };
    loadRestaurant();
  }, [restaurantId]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const sidebarContent = (
    <>
      <div className="h-16 border-b border-border/50 flex items-center px-4 gap-3">
        {(() => {
          const logoSrc = platformConfig.dashboard_icon_url || platformConfig.logo_url;
          return logoSrc ? (
            <img src={logoSrc} alt={platformConfig.logo_text} className="w-9 h-9 rounded-xl object-contain shrink-0" loading="eager" />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shrink-0 shadow-lg shadow-primary/20">
              <span className="text-primary-foreground font-bold text-xs">{platformConfig.logo_initials || "VM"}</span>
            </div>
          );
        })()}
        {(sidebarOpen || mobileSidebarOpen) && (
          <div className="min-w-0">
            <span className="font-display font-bold text-sm">{platformConfig.logo_text || "ViaMenu"}</span>
            {restaurantName && <p className="text-[9px] text-muted-foreground truncate">{restaurantName}</p>}
          </div>
        )}
      </div>
      <nav className="flex-1 p-2.5 space-y-3 overflow-y-auto">
        {sidebarSections.map((section) => {
          const visibleItems = section.items.filter(item => !item.featureKey || planLimits.hasFeature(item.featureKey));
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.title}>
              {(sidebarOpen || mobileSidebarOpen) && (
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                  {section.title}
                </p>
              )}
              {!sidebarOpen && !mobileSidebarOpen && (
                <div className="mx-auto my-1 w-5 border-t border-border/40" />
              )}
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.label}
                      onClick={() => {
                        navigate(item.path);
                        setMobileSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                        isActive
                          ? "bg-primary/10 text-primary font-medium shadow-sm"
                          : "text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
                      }`}
                    >
                      <item.icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? "text-primary" : ""}`} />
                      {(sidebarOpen || mobileSidebarOpen) && <span className="truncate">{item.label}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border/50 space-y-1">
        <div className="flex items-center justify-center gap-1">
          <ClearCacheButton />
          <ThemeToggle collapsed={!sidebarOpen && !mobileSidebarOpen} />
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" />
          {(sidebarOpen || mobileSidebarOpen) && <span>Sair</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <motion.aside
            initial={{ x: -280 }} animate={{ x: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-72 h-full bg-card border-r border-border flex flex-col z-10 shadow-2xl"
          >
            {sidebarContent}
          </motion.aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className={`hidden md:flex ${sidebarOpen ? "w-64" : "w-[68px]"} border-r border-border/50 bg-card/80 backdrop-blur-sm transition-all duration-300 flex-col shrink-0 sticky top-0 h-screen`}>
        {sidebarContent}
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto min-w-0">
        {/* Header with menu toggle */}
        <header className="h-12 border-b border-border/50 flex items-center px-4 bg-card/50 backdrop-blur-sm sticky top-0 z-30">
          <button
            onClick={() => window.innerWidth < 768 ? setMobileSidebarOpen(!mobileSidebarOpen) : setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
          >
            <Menu className="h-5 w-5 text-muted-foreground" />
          </button>
        </header>
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
